import { FunctionComponent } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import styles from './EstimatedCosts.module.css';

interface CostItem {
  label: string;
  billedAmount: string;
  insurancePays: string;
  patientCost: string;
  savingsPercentage?: number;
}

interface EstimatedCostsProps {
  insurancePlan: string;
  insuranceLogoUrl?: string;
  costs: CostItem[];
  dataSourceNote?: string;
}

const EstimatedCosts: FunctionComponent<EstimatedCostsProps> = ({
  insurancePlan,
  insuranceLogoUrl,
  costs,
  dataSourceNote = "Based on historical claims data from patients with similar insurance"
}) => {
  // Function to map insurance plan names to logo filenames
  const getInsuranceLogoUrl = (plan: string): string => {
    const logoMap: { [key: string]: string } = {
      'Blue Cross Blue Shield': 'bcbs.svg',
      'Aetna': 'aetna.svg',
      'Cigna': 'cigna.svg',
      'United Healthcare': 'uhc.svg',
      'UnitedHealthcare': 'UnitedHealthcare.svg',
      'Kaiser Permanente': 'kaiserpermanente.svg',
      'Humana': 'humana.svg',
      'Medicare': 'medicare.svg',
      'Medicaid': 'medicaid.svg',
      'Tricare': 'tricare.svg',
      'Oscar Health': 'oscarhealth.svg',
      'Molina Healthcare': 'molinahealthcare.svg',
      'Centene': 'centene.svg',
      'Anthem': 'elevancehealth.svg',
      'Elevance Health': 'elevancehealth.svg',
      'Health Net': 'healthnet.svg',
      'Emblem Health': 'emblemhealth.svg',
      'Bright Health': 'brighthealth.svg',
      'Workers Compensation': 'aig.svg'
    };

    const filename = logoMap[plan] || 'generic.svg';
    return `/api/insurance-logo/${filename}`;
  };

  const logoUrl = insuranceLogoUrl || getInsuranceLogoUrl(insurancePlan);

  return (
    <div className={styles.estimatedCosts}>
      <div className={styles.header}>
        <h3 className={styles.title}>Estimated Costs</h3>
        <div className={styles.insuranceInfo}>
          {logoUrl && (
            <img 
              src={logoUrl} 
              alt={insurancePlan} 
              className={styles.insuranceLogo}
              onError={(e) => {
                // Hide image if it fails to load
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          <span className={styles.insuranceName}>{insurancePlan}</span>
        </div>
      </div>
      
      <div className={styles.costsTable}>
        <div className={styles.tableHeader}>
          <div className={styles.columnHeader}>Service</div>
          <div className={styles.columnHeader}>Billed</div>
          <div className={styles.columnHeader}>Insurance Pays</div>
          <div className={styles.columnHeader}>You Pay</div>
        </div>
        
        {costs.map((cost, index) => (
          <div key={index} className={styles.costRow}>
            <div className={styles.serviceCell}>
              <span className={styles.serviceLabel}>{cost.label}</span>
            </div>
            <div className={styles.amountCell}>
              <span className={styles.billedAmount}>{cost.billedAmount}</span>
            </div>
            <div className={styles.amountCell}>
              <span className={styles.insuranceAmount}>{cost.insurancePays}</span>
            </div>
            <div className={styles.amountCell}>
              <span className={styles.patientAmount}>{cost.patientCost}</span>
              {cost.savingsPercentage && (
                <div className={styles.savings}>
                  <TrendingDownIcon className={styles.savingsIcon} />
                  <span className={styles.savingsText}>{cost.savingsPercentage}% less</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      
      <div className={styles.dataNote}>
        <InfoOutlinedIcon className={styles.infoIcon} />
        <span>{dataSourceNote}</span>
      </div>
    </div>
  );
};

export default EstimatedCosts;